import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext, StateToken } from '@ngxs/store';
import { insertItem, patch, removeItem, updateItem } from '@ngxs/store/operators';
import { tap } from 'rxjs';
import { AdminCrewManagementRoutes } from 'src/app/core/enums/routes.enum';
import { ApiResponse } from 'src/app/core/models/api-response';
import { OnSuccess } from 'src/app/shared/decorators/on-success.decorator';
import { ResetCurrentFilter } from 'src/app/core/state/main-table-filter.actions';
import { IActivityDropdown, IActivityResponse, map } from '../../models/activity.model';
import { ActivityApiService } from '../../services/activity-api.service';
import {
  CreateActivity,
  CreateActivityForTripBased,
  DeleteActivity,
  LoadActivities,
  LoadActivityById,
  LoadAllActivitiesForDepartment,
  UpdateActivity,
  ClearCurrentActivity,
} from './activity.actions';

export interface ActivitiesStateModel {
  activities: IActivityResponse[];
  allActivitiesForDepartment: IActivityDropdown[];
  currentActivity: IActivityResponse;
  activitiesTotalPages: number;
  lastCreatedActivityId: string;
}

const ACTIVITY_STATE_TOKEN = new StateToken<ActivitiesStateModel>('activities');

@State<ActivitiesStateModel>({
  name: ACTIVITY_STATE_TOKEN,
  defaults: {
    activities: [],
    allActivitiesForDepartment: [],
    currentActivity: <IActivityResponse>{},
    activitiesTotalPages: 0,
    lastCreatedActivityId: '',
  },
})
@Injectable()
export class ActivityState {
  constructor(private service: ActivityApiService) {}

  @Action(LoadActivities, { cancelUncompleted: true })
  loadActivities(
    { getState, setState }: StateContext<ActivitiesStateModel>,
    { filter }: LoadActivities
  ) {
    return this.service.getActivities(filter).pipe(
      tap((response: ApiResponse<IActivityResponse[]>) => {
        const state = getState();
        setState({
          ...state,
          activities: response.data,
          activitiesTotalPages: response.totalPages,
        });
      })
    );
  }

  @Action(LoadAllActivitiesForDepartment, { cancelUncompleted: true })
  loadAllActivitiesForDepartment(
    { getState, setState }: StateContext<ActivitiesStateModel>,
    { departmentId }: LoadAllActivitiesForDepartment
  ) {
    return this.service.getAllActivitiesForDepartment(departmentId).pipe(
      tap((response: IActivityDropdown[]) => {
        const state = getState();
        setState({
          ...state,
          allActivitiesForDepartment: response,
        });
      })
    );
  }

  @Action(LoadActivityById, { cancelUncompleted: true })
  loadActivityById(
    { getState, setState }: StateContext<ActivitiesStateModel>,
    { id }: LoadActivityById
  ) {
    return this.service.getActivityById(id).pipe(
      tap((response: IActivityResponse) => {
        const state = getState();
        setState({
          ...state,
          currentActivity: response,
        });
      })
    );
  }

  @Action(CreateActivity, { cancelUncompleted: true })
  @OnSuccess({ url: AdminCrewManagementRoutes.Activities, message: 'activityCreated' })
  createActivity(ctx: StateContext<ActivitiesStateModel>, { activity }: CreateActivity) {
    return this.service.createActivity(activity).pipe(
      tap((data: IActivityResponse) => {
        ctx.setState(
          patch({
            activities: insertItem<IActivityResponse>(data),
          })
        );
      })
    );
  }

  @Action(CreateActivityForTripBased, { cancelUncompleted: true })
  createActivityForTripBased(
    ctx: StateContext<ActivitiesStateModel>,
    { activity }: CreateActivityForTripBased
  ) {
    return this.service.createActivity(activity).pipe(
      tap((data: IActivityResponse) => {
        ctx.setState(
          patch({
            activities: insertItem<IActivityResponse>(data),
            lastCreatedActivityId: data.id,
          })
        );
      })
    );
  }

  @Action(UpdateActivity, { cancelUncompleted: true })
  @OnSuccess({ url: AdminCrewManagementRoutes.Activities, message: 'activityUpdated' })
  updateActivity(ctx: StateContext<ActivitiesStateModel>, { activity }: UpdateActivity) {
    return this.service.updateActivity(activity).pipe(
      tap((response: IActivityResponse) => {
        ctx.setState(
          patch({
            activities: updateItem<IActivityResponse>(
              (activityToUpdate: IActivityResponse | undefined) =>
                activityToUpdate?.id === activity.id,
              map(activity)
            ),
          })
        );
      })
    );
  }

  @Action(DeleteActivity, { cancelUncompleted: true })
  @OnSuccess({
    url: AdminCrewManagementRoutes.Activities,
    message: 'activityDeleted',
    dispatch: ResetCurrentFilter,
  })
  deleteActivity(ctx: StateContext<ActivitiesStateModel>, { id }: DeleteActivity) {
    return this.service.deleteActivity(id).pipe(
      tap(() => {
        ctx.setState(
          patch({
            activities: removeItem<IActivityResponse>(
              (activityToRemove: IActivityResponse | undefined) => activityToRemove?.id === id
            ),
          })
        );
      })
    );
  }

  @Action(ClearCurrentActivity)
  clearCurrentActivity(ctx: StateContext<ActivitiesStateModel>) {
    ctx.setState(patch({ currentActivity: undefined }));
  }

  @Selector()
  static getActivitiesTotalPages(state: ActivitiesStateModel) {
    return state.activitiesTotalPages;
  }

  @Selector()
  static getActivities(state: ActivitiesStateModel) {
    return state.activities;
  }

  @Selector()
  static getAllActivities(state: ActivitiesStateModel): IActivityDropdown[] {
    return state.allActivitiesForDepartment;
  }

  @Selector()
  static getActivityById(state: ActivitiesStateModel) {
    return state.currentActivity;
  }

  @Selector()
  static getLastCreatedActivityId(state: ActivitiesStateModel) {
    return state.lastCreatedActivityId;
  }
}
